import { ChangeDetectorRef, Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Timestamp } from 'firebase/firestore';
import { TableModule } from 'primeng/table';
import { Funciones } from '../services/funciones';
import { DataService } from '../services/data';
import { SupportService } from '../services/support.service';
import { Task } from '../models/task.model';
import { SupportTicket } from '../models/support-ticket.model';
import {
  countUnreadSupportTickets,
  firstUnreadSupportTicket,
  supportReadChanged$,
} from '../support/support-unread';
import { ButtonModule } from 'primeng/button';
import { filter, Subscription, switchMap, of } from 'rxjs';
import { TagModule } from 'primeng/tag';
import { BadgeModule } from 'primeng/badge';
import { MessageModule } from 'primeng/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../services/language.service';
import { MissionCurrencyService } from '../services/mission-currency.service';
import { MissionMoneyPipe } from '../pipes/mission-money.pipe';
import { EventsModel } from '../models/events.model';
import { FinancialTotals } from '../models/offering.model';
// CHARTS
import * as echarts from 'echarts/core';
import { NgxEchartsModule } from 'ngx-echarts';
import type { EChartsCoreOption } from 'echarts/core';
import { ThemeOption } from 'ngx-echarts';
import { CoolTheme } from '../themes/CoolTheme';
import { MacaronsTheme } from '../themes/MacaronsTheme';
import { BarChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
echarts.use([BarChart, GridComponent, CanvasRenderer]);


@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    FontAwesomeModule,
    TableModule,
    ButtonModule,
    CommonModule,
    TagModule,
    NgxEchartsModule,
    BadgeModule,
    MessageModule,
    TranslateModule,
    MissionMoneyPipe,
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
  encapsulation: ViewEncapsulation.None
})
export class Dashboard {

  private get dateLocale(): string {
    return this.languageService.getCurrentLang() === 'it' ? 'it-IT' : 'es-ES';
  }

  private formatMoney(value: number): string {
    const uiLang = this.languageService.getCurrentLang() === 'it' ? 'it' : 'es';
    return this.missionCurrency.format(value, { uiLang });
  }

  roseOptions: EChartsCoreOption = {
    title: { text: 'Nightingale Rose Diagram', left: 'left', textStyle: { fontSize: 12 } },
    tooltip: {
      trigger: 'item',
      valueFormatter: (v: number) => this.formatMoney(Number(v ?? 0))
    },
    legend: { bottom: 0, type: 'scroll' },
    series: [{
      name: 'Finanzas del mes',
      type: 'pie',
      radius: ['25%', '70%'],
      center: ['50%', '45%'],
      roseType: 'area',
      padAngle: 2,
      itemStyle: { borderRadius: 8 },
      label: {
        formatter: (p: any) => `${p.name}\n${this.formatMoney(Number(p.value ?? 0))}`,
        fontSize: 11, lineHeight: 14
      },
      data: [] // se llena con buildRoseData()
    }]
  };

  // usa merge para actualizar sin recrear el chart ni cambiar el theme
  roseMerge: EChartsCoreOption = {};

  theme: string | ThemeOption = 'coolTheme';
  macaronsTheme: string | ThemeOption = 'MacaronsTheme';
  coolTheme = CoolTheme;
  macarons = MacaronsTheme;
  options: EChartsCoreOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b} : {c} ({d}%)',
    },
    calculable: true,
    series: [
      {
        type: 'pie',
        radius: [30, 110],
        roseType: 'area',
        data: [
          { value: 80, name: 'rose1' },
          { value: 100, name: 'rose2' },
          { value: 120, name: 'rose3' },
          { value: 140, name: 'rose4' },
        ],
      },
    ],
  };

  memberOptions: EChartsCoreOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b} : {c} ({d}%)',
    },
    calculable: true,
    series: [
      {
        type: 'pie',
        radius: [30, 110],
        roseType: 'area',
        data: [
          { value: 80, name: 'rose1' },
          { value: 100, name: 'rose2' },
          { value: 120, name: 'rose3' },
          { value: 140, name: 'rose4' },
        ],
      },
    ],
  };

  chartOptions: any = {
    title: { text: 'Finanzas (mes)' },
    tooltip: {},
    xAxis: { type: 'category', data: ['Entradas', 'Ofrendas', 'Diezmos', 'Salidas'] },
    yAxis: { type: 'value' },
    series: [{ type: 'bar', data: [0, 0, 0, 0] }]
  };

  // Financial Summary
  public totalOfrendasMesActual: number = 0;
  public totalOfrendasDominicalesMesActual: number = 0;
  public totalDiezmosMesActual: number = 0;
  public totalSalidasMesActual: number = 0;
  public totalEntradasMesActual: number = 0;
  public totalSaldoEnCaja: number = 0;
  public totalSaldoMesAnterior: number = 0;

  // --- MEMBERSHIP CHART / Datos de membresía ---
  public members: any[] = [];
  public miembrosActivos = 0;
  public hombresCount = 0;
  public mujeresCount = 0;
  public familiasCount = 0;
  public visitantesCount = 0;
  public bautizadosCount = 0;
  public noReconocidosCount = 0;

  // merge para actualizar el chart sin recrearlo
  memberMerge: EChartsCoreOption = {};

  // Banner for unclosed previous month report
  showBanner: boolean = false;
  movementsBlocked = false;
  openPeriodsDisplay = '';
  previousMonthName: string = '';

  isSidenavExpanded: boolean = false;
  public rows: any[] = [];
  currentPage = 0;
  rowsPerPage = 10;
  totalRecords = 0;
  @ViewChild('dt') table: any;

  private subscriptions = new Subscription();

  events: EventsModel[] = [];
  upcomingEvents: EventsModel[] = [];

  tasks: Task[] = [];
  filteredTasks: Task[] = [];
  currentUserCategories: string[] = [];

  // FINANZAS
  totalFinanzas: FinancialTotals = {
    totalOfrendas: 0,
    totalDiezmos: 0,
    totalSalidas: 0,
    totalEntradas: 0,
    totalDisponible: 0
  }

  // Dashboard computed properties
  get totalMembersActive(): number {
    return this.miembrosActivos;
  }

  get pendingTasksCount(): number {
    return this.tasks.filter(t => t.status === 'Pendiente' || t.status === 'En Proceso').length;
  }

  get upcomingTasks(): Task[] {
    return this.tasks
      .filter(t => t.status === 'Pendiente' || t.status === 'En Proceso')
      .sort((a, b) => {
        // Sort by priority first (Alta > Media > Baja)
        const priorityOrder = { 'Alta': 0, 'Media': 1, 'Baja': 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;

        // Then by due date (safe parsing)
        const dateA = a.dueDateTime ? new Date(a.dueDateTime).getTime() : 0;
        const dateB = b.dueDateTime ? new Date(b.dueDateTime).getTime() : 0;
        return dateA - dateB;
      })
      .slice(0, 6); // Get top 6 tasks
  }

  get saldoDisponible(): number {
    return this.totalEntradasMesActual - this.totalSalidasMesActual;
  }

  get nextEventDate(): string {
    if (this.upcomingEvents.length > 0) {
      const date = this.upcomingEvents[0].date;
      return date instanceof Date ? date.toLocaleDateString(this.dateLocale) : '';
    }
    return '';
  }

  // Permissions
  userRoles: string[] = [];
  supportUnreadCount = 0;
  supportUnreadTicketId: string | null = null;
  private openSupportTickets: SupportTicket[] = [];
  private supportUid = '';
  private supportTicketsSub?: Subscription;

  constructor(
    private funciones: Funciones,
    private dataService: DataService,
    private supportService: SupportService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef,
    private readonly missionCurrency: MissionCurrencyService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.suscripciones();
  }

  suscripciones() {
    this.subscriptions.add(
      this.translate.onLangChange.subscribe(() => {
        this.updateRoseChartFromTotals();
        this.updateMemberChartFromData();
        this.cdr.markForCheck();
      })
    );

    // Subscribe to User Roles
    this.subscriptions.add(
      this.dataService.userRoles$.subscribe(roles => {
        this.userRoles = roles;
      })
    );

    this.subscriptions.add(
      this.dataService.offerings$
        .pipe(filter(ofs => Array.isArray(ofs)))
        .subscribe(offerings => {
          this.rows = offerings;
          this.recalculateTotals();
        })
    );

    this.subscriptions.add(
      this.dataService.members$
        .pipe(filter(ms => Array.isArray(ms)))
        .subscribe(ms => {
          this.computeMemberCounts(ms);
          this.updateMemberChartFromData();
        })
    );

    // Subscribe to tasks
    this.subscriptions.add(
      this.dataService.getTareas()
        .pipe(filter(tasks => Array.isArray(tasks)))
        .subscribe(tasks => {
          this.tasks = tasks;
        })
    );

    // Subscribe to banner for unclosed previous month report
    this.subscriptions.add(
      this.dataService.banner$.subscribe(show => {
        this.showBanner = show;
      })
    );

    this.subscriptions.add(
      this.dataService.churchId$.pipe(
        switchMap((churchId) => {
          this.supportTicketsSub?.unsubscribe();
          this.openSupportTickets = [];
          this.refreshSupportUnread();
          if (!churchId) return of([] as SupportTicket[]);
          return this.supportService.listenOpenTickets(churchId);
        })
      ).subscribe({
        next: (tickets) => {
          this.openSupportTickets = tickets || [];
          this.refreshSupportUnread();
        },
        error: () => {
          this.openSupportTickets = [];
          this.refreshSupportUnread();
        },
      })
    );

    this.subscriptions.add(
      supportReadChanged$.subscribe(() => this.refreshSupportUnread())
    );

    this.subscriptions.add(
      this.dataService.uidUser$.subscribe((uid) => {
        this.supportUid = uid || '';
        this.refreshSupportUnread();
      })
    );

    // Subscribe to previous month name
    this.subscriptions.add(
      this.dataService.openPeriodo$.subscribe(periodo => {
        const ym = periodo?.id || '';
        const name = this.funciones.formatYMtoText(ym);
        this.previousMonthName = name;
      })
    );

    this.subscriptions.add(
      this.dataService.periodosFinancierosState$.subscribe(state => {
        this.movementsBlocked = state.movementsBlocked;
        this.openPeriodsDisplay = state.openYms
          .map(ym => this.funciones.formatYMtoText(ym))
          .join(', ');
      })
    );
  }

  hasAccess(allowedRoles: string[]): boolean {
    return this.userRoles.some(role => allowedRoles.includes(role));
  }

  /** Datos base del rose chart; `nameKey` es clave ngx-translate (DASHBOARD.*). */
  private buildRoseData(): { nameKey: string; value: number; isExpense: boolean }[] {
    const ingresos = Number(this.totalEntradasMesActual || 0);
    const egresos = Number(this.totalSalidasMesActual || 0);
    const ofrendas = Number(this.totalOfrendasMesActual || 0) +
      Number(this.totalOfrendasDominicalesMesActual || 0);
    const diezmos = Number(this.totalDiezmosMesActual || 0);

    const data = [
      { nameKey: 'DASHBOARD.CHART_FIN_TOTAL_INCOME', value: ingresos, isExpense: false },
      { nameKey: 'DASHBOARD.CHART_FIN_TOTAL_OFFERINGS', value: ofrendas, isExpense: false },
      { nameKey: 'DASHBOARD.CHART_FIN_TITHES', value: diezmos, isExpense: false },
      { nameKey: 'DASHBOARD.CHART_FIN_TOTAL_EXPENSES', value: egresos, isExpense: true },
    ].filter(d => d.value > 0);

    data.sort((a, b) => b.value - a.value);
    return data;
  }

  private updateRoseChartFromTotals(): void {
    const raw = this.buildRoseData();
    const seriesName = this.translate.instant('DASHBOARD.CHART_FIN_SERIES_NAME');

    const data = raw.map(d => ({
      name: this.translate.instant(d.nameKey),
      value: d.value,
      ...(d.isExpense ? { itemStyle: { color: '#e74c3c' } } : {}),
    }));

    const baseSeries = {
      name: seriesName,
      type: 'pie' as const,
      radius: ['25%', '70%'],
      center: ['50%', '45%'],
      roseType: 'area' as const,
      padAngle: 2,
      itemStyle: { borderRadius: 8 }
    };

    const prevSeries =
      (this.options as any)?.series?.[0] ??
      (this.roseOptions as any)?.series?.[0] ?? {};

    const mergedSeries = {
      ...baseSeries,
      ...prevSeries,
      data,  // <-- aquí ya va EGRESOS en rojo
      label: {
        ...(prevSeries.label || {}),
        show: true,
        formatter: (p: any) => `{name|${p.name}}\n{val|${this.formatMoney(Number(p.value ?? 0))}}`,
        rich: {
          ...(prevSeries.label?.rich || {}),
          name: { ...(prevSeries.label?.rich?.name || {}), width: 96, lineHeight: 16, overflow: 'break', fontSize: 11 },
          val: { ...(prevSeries.label?.rich?.val || {}), fontSize: 11 }
        }
      },
      labelLine: { ...(prevSeries.labelLine || {}), length: 12, length2: 8 },
      avoidLabelOverlap: true,
      labelLayout: (prevSeries as any).labelLayout ?? ((_: any) => ({ moveOverlap: 'shiftY', alignTo: 'labelLine' })),
      minAngle: (prevSeries as any).minAngle ?? 6
    };

    this.options = {
      ...(this.options || {}),
      tooltip: {
        ...(this.options as any)?.tooltip,
        trigger: 'item',
        confine: true,
        extraCssText: 'white-space: normal; word-break: break-word; max-width: 300px; line-height: 1.4;',
        formatter: (p: any) => `${p.seriesName ? p.seriesName + '<br/>' : ''}${p.name}: ${this.formatMoney(Number(p.value ?? 0))}`
      },
      legend: { ...(this.options as any)?.legend, bottom: 0, type: 'scroll' },
      series: [mergedSeries]
    } as EChartsCoreOption;

    this.roseMerge = { series: [{ data }] };
  }

  private recalculateTotals(): void {
    let entradas = 0,
      salidas = 0,
      ofrendas = 0,
      ofrendasED = 0,
      diezmos = 0,
      saldoMesAnterior = 0,
      saldoEnCaja = 0;

    this.rows.forEach(mov => {
      const monto = mov.monto || 0;

      if (mov.tipoMovimiento === 'Ingreso') {

        // ✅ excluir ambos saldos de las entradas normales
        if (mov.concepto !== 'Saldo Inicial' && mov.concepto !== 'Saldo Mes Anterior') {
          entradas += monto;
        }

        if (mov.concepto === 'Ofrendas Diarias') ofrendas += monto;
        if (mov.concepto === 'Ofrendas Escuela Dominical') ofrendasED += monto;
        if (mov.concepto === 'Diezmo Iglesia') diezmos += monto;
        if (mov.concepto === 'Saldo Mes Anterior' || mov.concepto === 'Saldo Inicial') saldoMesAnterior += monto;

      } else if (mov.tipoMovimiento === 'Egreso') {
        salidas += monto;
      }
    });

    saldoEnCaja = saldoMesAnterior + entradas - salidas;

    this.totalEntradasMesActual = entradas;
    this.totalOfrendasDominicalesMesActual = ofrendasED;
    this.totalSalidasMesActual = salidas;
    this.totalOfrendasMesActual = ofrendas;
    this.totalDiezmosMesActual = diezmos;
    this.totalSaldoEnCaja = saldoEnCaja;
    this.totalSaldoMesAnterior = saldoMesAnterior;

    this.updateRoseChartFromTotals();
  }

  private buildMemberData(): { nameKey: string; value: number }[] {
    const data = [
      { nameKey: 'DASHBOARD.MEMBERS_ACTIVE', value: this.miembrosActivos },
      { nameKey: 'DASHBOARD.MEMBERS_MEN', value: this.hombresCount },
      { nameKey: 'DASHBOARD.MEMBERS_WOMEN', value: this.mujeresCount },
      { nameKey: 'DASHBOARD.MEMBERS_VISITORS', value: this.visitantesCount },
      { nameKey: 'DASHBOARD.MEMBERS_BAPTIZED', value: this.bautizadosCount },
      { nameKey: 'DASHBOARD.MEMBERS_NOT_RECOGNIZED', value: this.noReconocidosCount },
    ].filter(d => d.value > 0);

    data.sort((a, b) => b.value - a.value);
    return data;
  }

  private computeMemberCounts(members: any[]): void {
    const rawMembers = Array.isArray(members) ? members : [];

    // Filtra Pastores igual que en members.ts
    this.members = rawMembers.filter(m => {
      // Si tiene roles y es array, verificar si incluye 'Pastor'
      if (m.roles && Array.isArray(m.roles)) {
        return !m.roles.includes('Pastor');
      }
      return true; // Si no tiene roles o no es array, se incluye
    });

    this.miembrosActivos = this.members.filter(m => (m.estadoParticipacion ?? '').toLowerCase() === 'activo').length;
    this.hombresCount = this.members.filter(m => ((m.genero ?? '').toString().toLowerCase() === 'm')).length;
    this.mujeresCount = this.members.filter(m => ((m.genero ?? '').toString().toLowerCase() === 'f')).length;
    // Si tienes una propiedad de familia agrupar por familiaId para contar familias
    this.familiasCount = Array.from(new Set(this.members.map(m => m.familiaId).filter(Boolean))).length;
    this.visitantesCount = this.members.filter(m => (m.tipo ?? '').toLowerCase() === 'visitante').length;
    this.bautizadosCount = this.members.filter(m => m.bautismo === 'Bautizado').length;
    this.noReconocidosCount = this.members.filter(m => (m.estadoParticipacion ?? '').toLowerCase() === 'no reconocido').length;
  }

  private updateMemberChartFromData(): void {
    const raw = this.buildMemberData();
    const seriesName = this.translate.instant('DASHBOARD.CHART_MEM_SERIES_NAME');
    const data = raw.map(d => ({
      name: this.translate.instant(d.nameKey),
      value: d.value,
    }));

    const baseSeries = {
      name: seriesName,
      type: 'pie' as const,
      radius: ['30%', '60%'],
      center: ['50%', '45%'],
      avoidLabelOverlap: true,
      itemStyle: { borderRadius: 6 }
    };

    const prevSeries = (this.memberOptions as any)?.series?.[0] ?? {};
    const mergedSeries = {
      ...baseSeries,
      ...prevSeries,
      data,
      label: {
        ...(prevSeries.label || {}),
        show: true,
        formatter: (p: any) => `${p.name}\n${p.value}`,
        fontSize: 11
      }
    };

    this.memberOptions = {
      ...(this.memberOptions || {}),
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: (p: any) => `${p.name}: ${p.value}`
      },
      legend: { bottom: 0, type: 'scroll' },
      series: [mergedSeries]
    } as EChartsCoreOption;

    // merge para actualizaciones incrementales si usas [merge] en el template
    this.memberMerge = { series: [{ data }] };
  }

  filterUpcomingEvents(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.upcomingEvents = this.events.filter(event => new Date(event.date) >= today)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  onSidenavToggle(event: boolean) {
    this.isSidenavExpanded = event;
  }

  public viewTask(row: Task): void {
  }

  public deleteTask(row: Task): void {
  }

  convertDate(date: string): string {
    const fecha = new Date(date);
    const fechaTimestamp = Timestamp.fromDate(fecha);
    return this.funciones.convertirFechaHoraMinutos(fechaTimestamp, true);
  }

  prev() {
    if (this.currentPage > 0) {
      this.currentPage--;
      // Aquí actualiza tu lógica de paginación o recarga los datos
    }
  }

  next() {
    if (!this.isLastPage()) {
      this.currentPage++;
      // Aquí actualiza tu lógica de paginación o recarga los datos
    }
  }

  isFirstPage(): boolean {
    return this.currentPage === 0;
  }

  isLastPage(): boolean {
    return (this.currentPage + 1) * this.rowsPerPage >= this.totalRecords;
  }

  reset() {
    if (this.table) {
      this.table.reset();
    }
  }

  get currentDate(): string {
    return new Date().toLocaleDateString(this.dateLocale, { month: 'long', year: 'numeric' });
  }

  getPriority(status: string): "success" | "info" | "warn" | "danger" | "secondary" | "contrast" | undefined {
    switch (status) {
      case 'Alta':
        return 'danger';
      case 'Media':
        return 'warn';
      case 'Baja':
        return 'success';
      default:
        return 'info';
    }
  }

  getStatus(status: string): string {
    const cleanStatus = status?.trim()?.toLowerCase();
    switch (cleanStatus) {
      case 'pendiente':
        return 'danger';
      case 'en proceso':
        return 'info';
      case 'realizada':
        return 'success';
      case 'suspendida':
        return 'warn';
      default:
        return 'info';
    }
  }

  ngOnDestroy(): void {
    this.supportTicketsSub?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  private refreshSupportUnread(): void {
    const effectiveUid =
      this.supportUid || this.openSupportTickets[0]?.createdByUid || '';
    this.supportUnreadCount = countUnreadSupportTickets(
      this.openSupportTickets,
      effectiveUid
    );
    this.supportUnreadTicketId =
      firstUnreadSupportTicket(this.openSupportTickets, effectiveUid)?.id || null;
    this.cdr.markForCheck();
  }

  openSupportUnread(): void {
    if (this.supportUnreadCount === 1 && this.supportUnreadTicketId) {
      void this.router.navigate(['/dashboard/support', this.supportUnreadTicketId]);
      return;
    }
    void this.router.navigate(['/dashboard/support']);
  }
}
